import React, { useEffect, useMemo } from 'react';
import {
    TransferReceiptExpenseInputQuery,
    useTransferReceiptExpenseInputLazyQuery,
} from '../../../../../../../../services';
import { RhSingleAutocomplete } from '../../../../../../../dum';
import { FROM_ACCOUNT, ITransfer, TO_ACCOUNT } from '../../transfer-form';
import { useFormContext, useWatch } from 'react-hook-form';
import {
    AMOUNT,
    EXPENSE,
    INITIAL_AMOUNT,
    INITIAL_EXPENSE_ID,
    SUGGESTED_AMOUNT,
    TOTAL,
    TRANSFER_RECEIPTS,
} from '../transfer-receipt-columns';
import { useCalculateTransferReceiptsTotal } from '../../use-calculate-transfer-receipt-total';
import {
    FIELD_LABELS,
    formatFloat,
    round,
    shortLabel,
} from '../../../../../../../../helpers';

interface IResourceInput {
    index: number;
}

export function ExpensesInput({ index }: IResourceInput) {
    const [query, { data }] = useTransferReceiptExpenseInputLazyQuery();
    const { calculateTransferReceiptsTotal } =
        useCalculateTransferReceiptsTotal();
    const INPUT_NAME = `${TRANSFER_RECEIPTS}.${index}.${EXPENSE}`;
    const toAccount = useWatch({
        name: TO_ACCOUNT,
    }) as ITransfer['to_account'];
    const fromAccount = useWatch({
        name: FROM_ACCOUNT,
    }) as ITransfer['from_account'];
    // Formality of the participating OWN account (the expense's counterpart is a
    // supplier, so the own account is the "from" side on a supplier payment; the
    // generic find also covers adjustments). undefined → do not filter.
    const ownIsInformal = fromAccount?.is_own
        ? fromAccount.is_informal_account
        : toAccount?.is_own
        ? toAccount.is_informal_account
        : undefined;
    const initialExpenseId = useWatch({
        name: `${TRANSFER_RECEIPTS}.${index}.${INITIAL_EXPENSE_ID}`,
    }) as ITransfer['transfer_receipts'][number]['initial_expense_id'];
    const rx = useFormContext();

    useEffect(() => {
        if (toAccount?.is_supplier) {
            void query({
                variables: {
                    account_id: toAccount.id,
                },
            });
        }
    }, [query, toAccount]);

    const expenses = useMemo(() => {
        const rawExpensesUnfiltered: TransferReceiptExpenseInputQuery['getExpenses'] =
            data?.getExpenses || [];
        // Informal/formal rule: only offer expenses whose receipt-type formality
        // matches the participating own account. Skipped when there is no own
        // account or its flag is unknown.
        const rawExpenses =
            typeof ownIsInformal === 'boolean'
                ? rawExpensesUnfiltered.filter(
                      (expense) =>
                          !!expense.receipt_type?.is_informal_receipt ===
                          ownIsInformal,
                  )
                : rawExpensesUnfiltered;
        // Append the total (iva incluido) to the visible label so the user can
        // weigh each expense's size without opening it. `option_label` is a
        // display-only field; selection/persistence still keys off `id`.
        //
        // Codeless expenses have an empty compound_external_code, which would leave
        // the label as just " ($total)". Fall back to the expense internal_code so
        // there is always an identifier in front, and to the id when internal_code
        // is missing. This is a selector-only fallback — the expense stays codeless
        // in the DB.
        return rawExpenses.map((expense) => {
            const code =
                expense.compound_external_code ||
                `${expense.internal_code || expense.id}`;
            return {
                ...expense,
                option_label: `${code} ($${formatFloat(
                    expense.total_with_tax || 0,
                )})`,
            };
        });
    }, [data, ownIsInformal]);
    return (
        <RhSingleAutocomplete
            itemText={'option_label'}
            itemValue={'id'}
            options={expenses}
            highlightItemValue={initialExpenseId ?? undefined}
            name={INPUT_NAME}
            label={shortLabel(FIELD_LABELS.expense_id)}
            disableGutters
            returnObject
            onChange={(val: typeof expenses[number]) => {
                rx.setValue(
                    `${TRANSFER_RECEIPTS}.${index}.${TOTAL}`,
                    val.total_with_tax || 0,
                    {
                        shouldValidate: true,
                    },
                );

                // Remaining balance still owed on this expense = its total minus
                // whatever has already been allocated to it by other transfers.
                const suggested = round(
                    (val.total_with_tax || 0) -
                        (val.transfer_receipts_total || 0),
                );
                rx.setValue(
                    `${TRANSFER_RECEIPTS}.${index}.${SUGGESTED_AMOUNT}`,
                    suggested,
                    { shouldValidate: false },
                );

                const isEditing = !!rx.getValues('id');

                if (initialExpenseId != null && val.id === initialExpenseId) {
                    // Re-selected the expense this receipt was loaded with →
                    // restore the amount it had at form init (not the balance).
                    const initialAmount = rx.getValues(
                        `${TRANSFER_RECEIPTS}.${index}.${INITIAL_AMOUNT}`,
                    );
                    rx.setValue(
                        `${TRANSFER_RECEIPTS}.${index}.${AMOUNT}`,
                        initialAmount ?? 0,
                        { shouldValidate: true },
                    );
                } else if (!isEditing) {
                    // Brand-new transfer: pre-fill the remaining balance to save
                    // typing. (While editing a different expense, we leave the
                    // existing amount untouched and only offer the hint.)
                    rx.setValue(
                        `${TRANSFER_RECEIPTS}.${index}.${AMOUNT}`,
                        suggested,
                        { shouldValidate: true },
                    );
                }

                calculateTransferReceiptsTotal({ index });
            }}
        />
    );
}
